import { InputGroup, Label } from 'components/lib'
import React, { useState, useEffect, useCallback } from 'react'
import { AssistantConfiguration } from '@super-software-inc/foundation'
import AsyncSelect from 'react-select/async'
import { ActionMeta, SingleValue } from 'react-select'
import styled from 'styled-components/macro'
import { FaPlay, FaStop, FaSpinner } from 'react-icons/fa'
import { toast } from 'react-toastify'
import { SelectableOption } from 'components/lib/SelectableOption'
import { fetchVoices } from '../../../services/elevenLabs'
import {
  SPEECH_TO_TEXT_OPTIONS,
  LANGUAGE_MODEL_OPTIONS,
  ModelOption,
} from './modelConstants'

export interface VoiceOption {
  id: string
  name: string
  accent: 'american' | 'british' | 'irish' | 'australian'
  previewUrl: string
  labels: {
    description: string
    age: string
    gender: 'male' | 'female'
    use_case: string
  }
}

interface AudioState {
  isPlaying: boolean
  isLoading: boolean
  error: string | null
}

interface SelectOption {
  value: string
  label: string
  data: VoiceOption
}

const StyledSelectContainer = styled(AsyncSelect<SelectOption>)`
  max-width: 300px;
  margin-bottom: 1rem;
`

const VoiceOptionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
`

const PlayButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: rgb(20, 18, 50);
  padding: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  transition: color 0.3s ease-in-out;

  &:hover {
    color: ${props =>
      props.disabled
        ? 'rgb(20, 18, 50)'
        : props.theme.colors.secondaryPurple600};
  }

  &:disabled {
    cursor: not-allowed;
  }
`

const LabelPill = styled.span`
  display: inline-block;
  background-color: ${props => props.theme.colors.bg200};
  color: ${props => props.theme.colors.text0};
  font-size: 0.75rem;
  padding: 0.15rem 0.3rem;
  border-radius: 1rem;
  margin-right: 0.25rem;
`

const LabelsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.25rem;
`

const StyledModelMeta = styled.span`
  margin-right: 0.25rem;
`

const SpinnerIcon = styled(FaSpinner)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

const getAccentEmoji = (
  accent: 'american' | 'british' | 'irish' | 'australian',
) => {
  switch (accent) {
    case 'american':
      return '🇺🇸'
    case 'british':
      return '🇬🇧'
    case 'irish':
      return '🇮🇪'
    case 'australian':
      return '🇦🇺'
    default:
      return '🇺🇸'
  }
}

const ModelOptionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
`

const ModelMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const ServiceLabel = styled.span`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text100};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
`

interface CustomizeVoiceAgentOptionsProps {
  values: Partial<AssistantConfiguration> & {
    voiceModelId?: string
    voiceModelName?: string
    voiceModelSTT?: string
    voiceModelLLM?: string
  }
  setFieldValue: (field: string, value: any) => void
}

const CustomizeVoiceAgentOptions: React.FC<CustomizeVoiceAgentOptionsProps> = ({
  values,
  setFieldValue,
}) => {
  // Get values from the form fields
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>(
    values.voiceModelId || '',
  )
  const [selectedSpeechToText, setSelectedSpeechToText] =
    useState<ModelOption | null>(
      values.voiceModelSTT
        ? SPEECH_TO_TEXT_OPTIONS.find(
            option => option.model === values.voiceModelSTT,
          ) || null
        : null,
    )
  const [selectedLanguageModel, setSelectedLanguageModel] =
    useState<ModelOption | null>(
      values.voiceModelLLM
        ? LANGUAGE_MODEL_OPTIONS.find(
            option => option.model === values.voiceModelLLM,
          ) || null
        : null,
    )
  const [displayedVoices, setDisplayedVoices] = useState<VoiceOption[]>([])
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null)
  const [audioStates, setAudioStates] = useState<Record<string, AudioState>>({})
  const [defaultOptions, setDefaultOptions] = useState<
    Array<{ value: string; label: string; data: VoiceOption }>
  >([])
  const [dropdownValue, setDropdownValue] = useState<{
    value: string
    label: string
    data: VoiceOption
  } | null>(null)

  // Load initial options with a simulated network delay
  useEffect(() => {
    const loadInitialOptions = async () => {
      try {
        const voices = await fetchVoices()
        const selectOptions = voices.map(voice => ({
          value: voice.id,
          label: `${voice.name} (${voice.accent})`,
          data: voice,
        }))
        setDefaultOptions(selectOptions)

        // If we already have a voiceModelId in the values, try to find the matching voice
        if (values.voiceModelId) {
          const existingVoice = voices.find(
            voice => voice.id === values.voiceModelId,
          )
          if (existingVoice) {
            setDisplayedVoices(prev => [...prev, existingVoice])
            setSelectedVoiceId(existingVoice.id)
            setAudioStates(prev => ({
              ...prev,
              [existingVoice.id]: {
                isPlaying: false,
                isLoading: false,
                error: null,
              },
            }))
            return // Don't show first voice if we found a match
          }
        }

        // Only show the first voice if we don't have an existing selected voice
        if (voices.length > 0) {
          setDisplayedVoices([voices[0]])
          // Only set selected ID if not already set
          if (!selectedVoiceId) {
            setSelectedVoiceId(voices[0].id)
          }
          // Initialize audio state for the first voice
          setAudioStates({
            [voices[0].id]: {
              isPlaying: false,
              isLoading: false,
              error: null,
            },
          })
        }
      } catch (error) {
        toast.error('Error loading voices, please refresh the page')
      }
    }

    loadInitialOptions()
    // Only run once on component mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadOptions = (inputValue: string) =>
    new Promise<Array<{ value: string; label: string; data: VoiceOption }>>(
      resolve => {
        const filtered = defaultOptions.filter(
          (option: { value: string; label: string; data: VoiceOption }) =>
            option.data.name.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.data.labels.description
              .toLowerCase()
              .includes(inputValue.toLowerCase()),
        )
        resolve(filtered)
      },
    )

  const stopAllAudio = useCallback(() => {
    if (audioRef) {
      audioRef.pause()
      audioRef.currentTime = 0
    }
    setAudioStates(prev => {
      const newStates = { ...prev }
      Object.keys(newStates).forEach(key => {
        newStates[key] = {
          ...newStates[key],
          isPlaying: false,
          isLoading: false,
        }
      })
      return newStates
    })
  }, [audioRef])

  const handlePlayPreview = async (voiceId: string, previewUrl: string) => {
    try {
      // Stop any currently playing audio
      stopAllAudio()

      // Update loading state
      setAudioStates(prev => ({
        ...prev,
        [voiceId]: { ...prev[voiceId], isLoading: true, error: null },
      }))

      let audio = audioRef
      if (!audio) {
        audio = new Audio()
        setAudioRef(audio)
      }

      audio.src = previewUrl
      await audio.play()

      audio.onended = () => {
        setAudioStates(prev => ({
          ...prev,
          [voiceId]: { ...prev[voiceId], isPlaying: false, isLoading: false },
        }))
      }

      setAudioStates(prev => ({
        ...prev,
        [voiceId]: { ...prev[voiceId], isPlaying: true, isLoading: false },
      }))
    } catch (error) {
      setAudioStates(prev => ({
        ...prev,
        [voiceId]: {
          isPlaying: false,
          isLoading: false,
          error: 'Failed to play audio',
        },
      }))
    }
  }

  const handleVoiceBlockClick = (voice: VoiceOption) => {
    setSelectedVoiceId(voice.id)
    // Save the voice ID to both fields
    setFieldValue('voiceModelId', voice.id)
    setFieldValue('voiceModelName', voice.name)
    stopAllAudio()
  }

  const handleDropdownChange = (
    newValue: SingleValue<{ value: string; label: string; data: VoiceOption }>,
    actionMeta: ActionMeta<{ value: string; label: string; data: VoiceOption }>,
  ) => {
    if (newValue) {
      const newVoice = newValue.data

      // Add the new voice to displayed voices if it's not already there
      setDisplayedVoices(prev => {
        if (!prev.find(voice => voice.id === newVoice.id)) {
          return [...prev, newVoice]
        }
        return prev
      })

      // Initialize audio state for the new voice if it doesn't exist
      setAudioStates(prev => ({
        ...prev,
        [newVoice.id]: {
          isPlaying: false,
          isLoading: false,
          error: null,
        },
      }))

      // Automatically select the newly added voice
      handleVoiceBlockClick(newVoice)

      // Clear the dropdown selection
      setDropdownValue(null)
    }
  }

  // Update handlers for model selections
  const handleSpeechToTextSelect = (option: ModelOption) => {
    setSelectedSpeechToText(option)
    setFieldValue('voiceModelSTT', option.model)
    // Remove the automatic setting of TTS to match STT
  }

  const handleLanguageModelSelect = (option: ModelOption) => {
    setSelectedLanguageModel(option)
    setFieldValue('voiceModelLLM', option.model)
  }

  return (
    <div className="customize-voice-container">
      <InputGroup>
        <Label>Voice</Label>
        <div className="voice-select-container">
          <StyledSelectContainer
            id="voice-select"
            loadOptions={loadOptions}
            onChange={handleDropdownChange}
            placeholder="Search for a voice..."
            isClearable
            className="voice-select"
            value={dropdownValue}
            defaultOptions={defaultOptions}
            cacheOptions
          />
        </div>

        <VoiceOptionsRow>
          {displayedVoices.map(voice => (
            <SelectableOption
              key={voice.id}
              isSelected={voice.id === selectedVoiceId}
              onClick={() => handleVoiceBlockClick(voice)}
              title={voice.name}
              subtitle={getAccentEmoji(voice.accent)}
            >
              <PlayButton
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  const audioState = audioStates[voice.id]
                  if (audioState?.isPlaying) {
                    stopAllAudio()
                  } else {
                    handlePlayPreview(voice.id, voice.previewUrl)
                  }
                }}
                disabled={audioStates[voice.id]?.isLoading}
              >
                {audioStates[voice.id]?.isLoading ? (
                  <SpinnerIcon />
                ) : audioStates[voice.id]?.isPlaying ? (
                  <FaStop />
                ) : (
                  <FaPlay />
                )}
              </PlayButton>
              <LabelsContainer>
                {voice.labels.description && (
                  <LabelPill>{voice.labels.description}</LabelPill>
                )}
                {voice.labels.use_case && (
                  <LabelPill>{voice.labels.use_case}</LabelPill>
                )}
              </LabelsContainer>
            </SelectableOption>
          ))}
        </VoiceOptionsRow>
      </InputGroup>

      <InputGroup>
        <Label>Speech to text</Label>
        <ModelOptionsRow>
          {SPEECH_TO_TEXT_OPTIONS.map(option => (
            <SelectableOption
              key={`${option.service}-${option.model}`}
              isSelected={selectedSpeechToText?.model === option.model}
              onClick={() => handleSpeechToTextSelect(option)}
              title={option.modelDisplay}
              subtitle={option.icon}
            >
              <ModelMeta>
                <ServiceLabel>{option.serviceDisplay}</ServiceLabel>
              </ModelMeta>
            </SelectableOption>
          ))}
        </ModelOptionsRow>
      </InputGroup>

      <InputGroup>
        <Label>Language model</Label>
        <ModelOptionsRow>
          {LANGUAGE_MODEL_OPTIONS.map(option => (
            <SelectableOption
              key={`${option.service}-${option.model}`}
              isSelected={selectedLanguageModel?.model === option.model}
              onClick={() => handleLanguageModelSelect(option)}
              title={option.modelDisplay}
            >
              <ModelMeta>
                <ServiceLabel>
                  <StyledModelMeta>{option.serviceDisplay}</StyledModelMeta>
                  <span style={{ color: 'gray', padding: '0 0.15rem' }}>|</span>
                  <span style={{ fontSize: '0.75rem' }}>{option.icon}</span>
                  <span>{option.metaInformation}</span>
                </ServiceLabel>
              </ModelMeta>
            </SelectableOption>
          ))}
        </ModelOptionsRow>
      </InputGroup>
    </div>
  )
}

export default CustomizeVoiceAgentOptions
